import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Item } from '../components/common/Rest/Services/Data';
import { useAuth } from '../components/common/Auth/AuthProvider';
import cartsServiceInstance, { SaveItemRequest } from '../components/common/Rest/Services/CartService/CartsService';
import { mergeCarts } from '../components/common/Utils/MergeCarts';

interface CartContextType {
  cartItems: Item[];
  cartCount: number;
  cartTotal: number;
  addToCart: (item: Item, count?: number) => void;
  setItemCount: (itemId: number, count: number) => void;
  removeFromCart: (itemId: number) => void;
  isInCart: (itemId: number) => boolean;
  clearCart: () => void;
  isCartModalOpen: boolean;
  openCartModal: () => void;
  closeCartModal: () => void;
  clearLocalCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const readLocalCart = (): Item[] => {
  try {
    return JSON.parse(localStorage.getItem('cart') || '[]');
  } catch {
    return [];
  }
};

// Товари, явно прибрані з кошика локально (наприклад, поки токен був протухлий і
// SetCart на сервер не йшов) — щоб їх не "воскресив" наступний мердж із бекендом.
const readRemovedIds = (): Set<number> => {
  try {
    const raw: number[] = JSON.parse(localStorage.getItem('cart_removed_ids') || '[]');
    return new Set(raw);
  } catch {
    return new Set();
  }
};

const writeRemovedIds = (ids: Set<number>) => {
  localStorage.setItem('cart_removed_ids', JSON.stringify(Array.from(ids)));
};

const toSaveRequests = (items: Item[]): SaveItemRequest[] =>
  items.map((item) => ({
    itemId: item.id,
    count: item.count || 1,
  }));

const isRentable = (item: Item) =>
  Number.isFinite(item.stockCount) && item.stockCount > 0;

const getMaxRentCount = (item: Item) =>
  isRentable(item) ? item.stockCount : 0;

const clampRentCount = (item: Item, count: number) =>
  Math.max(0, Math.min(getMaxRentCount(item), count));

const normalizeCartItem = (item: Item): Item => ({
  ...item,
  count: clampRentCount(item, item.count || 1),
});

const normalizeCartItems = (items: Item[]): Item[] =>
  items.map(normalizeCartItem).filter((item) => item.count > 0);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cartItems, setCartItems] = useState<Item[]>([]);
  const [isCartModalOpen, setCartModalOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const isInitialLoad = useRef(true);
  const prevIsAuthenticated = useRef(isAuthenticated);

  useEffect(() => {
    setCartItems(normalizeCartItems(readLocalCart()));
  }, []);

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }

    localStorage.setItem('cart', JSON.stringify(cartItems));

    // Skip the save on the exact render where auth just flipped to true: the merge
    // effect below owns syncing to the server for that transition. Saving here too
    // would race it with the pre-merge (guest-only) cart and could overwrite the
    // correctly merged result if this request resolves last.
    const authJustChanged = prevIsAuthenticated.current !== isAuthenticated;
    prevIsAuthenticated.current = isAuthenticated;

    if (isAuthenticated && !authJustChanged) {
      cartsServiceInstance
        .SetCart(toSaveRequests(cartItems))
        .catch(() => undefined);
    }
  }, [cartItems, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const loadMergedCart = async () => {
      try {
        const backendCart = await cartsServiceInstance.GetCart();
        const localCart = readLocalCart();
        const removedIds = readRemovedIds();
        const mergedCartRequests = mergeCarts(backendCart, localCart, removedIds);

        const allItemsMap = new Map<number, Item>();
        backendCart.forEach((item) => allItemsMap.set(item.id, item));
        localCart.forEach((item) => allItemsMap.set(item.id, item));

        const mergedItems = normalizeCartItems(mergedCartRequests
          .map((req) => {
            const fullItem = allItemsMap.get(req.itemId);
            return fullItem ? normalizeCartItem({ ...fullItem, count: req.count }) : null;
          })
          .filter((item): item is Item => Boolean(item)));

        setCartItems(mergedItems);
        localStorage.setItem('cart', JSON.stringify(mergedItems));
        await cartsServiceInstance.SetCart(toSaveRequests(mergedItems));
        writeRemovedIds(new Set());
      } catch (error) {
        setCartItems(normalizeCartItems(readLocalCart()));
      }
    };

    loadMergedCart();
  }, [isAuthenticated]);

  const unmarkRemoved = (itemId: number) => {
    const removedIds = readRemovedIds();
    if (removedIds.delete(itemId)) {
      writeRemovedIds(removedIds);
    }
  };

  const markRemoved = (itemId: number) => {
    const removedIds = readRemovedIds();
    removedIds.add(itemId);
    writeRemovedIds(removedIds);
  };

  const addToCart = (item: Item, count = 1) => {
    if (!isRentable(item)) return;

    unmarkRemoved(item.id);
    setCartItems((prev) => {
      const existing = prev.find((cartItem) => cartItem.id === item.id);
      if (!existing) {
        return [...prev, normalizeCartItem({ ...item, count })];
      }

      return prev.map((cartItem) =>
        cartItem.id === item.id
          ? { ...cartItem, count: clampRentCount(cartItem, (cartItem.count || 1) + count) }
          : cartItem
      );
    });
  };

  const setItemCount = (itemId: number, count: number) => {
    if (count <= 0) {
      markRemoved(itemId);
    } else {
      unmarkRemoved(itemId);
    }

    setCartItems((prev) => {
      if (count <= 0) {
        return prev.filter((item) => item.id !== itemId);
      }

      return prev.map((item) => (item.id === itemId ? { ...item, count: clampRentCount(item, count) } : item));
    });
  };

  const removeFromCart = (itemId: number) => {
    markRemoved(itemId);
    setCartItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const isInCart = (itemId: number) => cartItems.some((item) => item.id === itemId);

  const clearCart = () => {
    setCartItems([]);
    writeRemovedIds(new Set());
    if (isAuthenticated) {
      cartsServiceInstance.SetCart([]).catch(() => undefined);
    }
  };

  const clearLocalCart = () => {
    setCartItems([]);
    localStorage.removeItem('cart');
    localStorage.removeItem('cart_removed_ids');
  };

  const openCartModal = () => setCartModalOpen(true);
  const closeCartModal = () => setCartModalOpen(false);

  const cartCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + (item.count || 1), 0),
    [cartItems]
  );

  const cartTotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.price * (item.count || 1), 0),
    [cartItems]
  );

  return (
    <CartContext.Provider
      value={{
        cartItems,
        cartCount,
        cartTotal,
        addToCart,
        setItemCount,
        removeFromCart,
        isInCart,
        clearCart,
        isCartModalOpen,
        openCartModal,
        closeCartModal,
        clearLocalCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
